import { useMemo } from "react";
import type { IContact } from "../types/Contacts";
import type { IChatMessage } from "../types/IChatMessages";
import { useChat } from "./chatStore";
import { toChatMessage, toContact } from "./mapping";

const NONE: IChatMessage[] = [];

/** The chat store, shaped the way the Teams-style screens already expect it: people, one thread per person, unread per person. */
export function useChatData(meContact: IContact) {
  const users = useChat((s) => s.users);
  const presence = useChat((s) => s.presence);
  const convs = useChat((s) => s.convs);
  const msgs = useChat((s) => s.msgs);
  const dmOf = useChat((s) => s.dmOf);
  const typing = useChat((s) => s.typing);
  const hasMore = useChat((s) => s.hasMore);
  const connection = useChat((s) => s.loaded);

  const contactList = useMemo<IContact[]>(
    () =>
      Object.values(users)
        .filter((u) => u.id !== meContact._id)
        .map((u) => toContact(u, presence[u.id]))
        .sort((a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`)),
    [users, presence, meContact._id],
  );
  const contactById = useMemo(() => {
    const m: Record<string, IContact> = {};
    contactList.forEach((c) => (m[c._id] = c));
    return m;
  }, [contactList]);

  const threads = useMemo(() => {
    const out: Record<string, IChatMessage[]> = {};
    for (const [partnerId, convId] of Object.entries(dmOf)) {
      const list = msgs[convId];
      if (!list || list.length === 0) continue;
      const partner = contactById[partnerId];
      const partnerReadSeq = convs[convId]?.partnerRead ?? 0;
      out[partnerId] = list.map((m) => toChatMessage(m, { me: meContact, partner, partnerReadSeq, sendState: m.sendState }));
    }
    return out;
  }, [dmOf, msgs, convs, contactById, meContact]);

  const unreadCounts = useMemo(() => {
    const out: Record<string, number> = {};
    for (const [partnerId, convId] of Object.entries(dmOf)) {
      const n = convs[convId]?.view.unread ?? 0;
      if (n > 0) out[partnerId] = n;
    }
    return out;
  }, [dmOf, convs]);

  /** People you already have a conversation with (even an empty one). */
  const hasConversation = useMemo(() => new Set(Object.keys(dmOf)), [dmOf]);

  const typingNames = (partnerId: string): string => {
    const convId = dmOf[partnerId];
    if (!convId) return "";
    const t = typing[convId];
    if (!t) return "";
    const now = Date.now();
    return Object.entries(t)
      .filter(([, until]) => until > now)
      .map(([uid]) => contactById[uid]?.firstName || "Someone")
      .join(", ");
  };

  return { contactList, contactById, threads, unreadCounts, hasConversation, typingNames, hasMoreFor: (partnerId: string) => !!hasMore[dmOf[partnerId] ?? ""], dmOf, loaded: connection, none: NONE };
}
